import { ICustomerPortalTheme } from "@smartrr/shared/entities/CustomerPortalTheme";
import { ICustomerRelationship } from "@smartrr/shared/entities/CustomerRelationship";
import { IOrganization } from "@smartrr/shared/entities/Organization";
import { IPaymentMethodResponse } from "@smartrr/shared/entities/PaymentMethod";
import { IShopifyLiquidCustomer } from "@smartrr/shared/shopifyLiquid/customer";
import { ICustomerPortalValidationObj } from "@smartrr/shared/shopifyLiquid/validation";
import { defaultCustomerPortalThemeConfig } from "@smartrr/shared/themes/defaultThemeConfig";
import { getFromLocalStorage, setInLocalStorage } from "@smartrr/shared/utils/localStorageAccess";

import logger from "@customer-app/shared/logger";
import { CustomerSubReducer } from "@customer-app/state/typedCustomerRedux";

export interface IAuthReducerState {
  isLoading: boolean;
  customerRelationship: ICustomerRelationship | null;
  organization: IOrganization | null;
  liquidCustomer: IShopifyLiquidCustomer | null;
  validation: ICustomerPortalValidationObj | null;
  paymentMethods: IPaymentMethodResponse[];
  theme: ICustomerPortalTheme;
}

const initialState: IAuthReducerState = {
  isLoading: true,
  customerRelationship: null,
  organization: null,
  liquidCustomer: null,
  validation: null,
  paymentMethods: [],
  theme: defaultCustomerPortalThemeConfig,
};

export const validationInLocalStorageKey = "_validation_";

export const isAdminPreviewingAsCustomer = (): string => {
  const validationFromLocalStorage = JSON.parse(getFromLocalStorage(validationInLocalStorageKey) ?? "{}");
  return validationFromLocalStorage?.adminShopifyId as string;
};

export const authReducer: CustomerSubReducer<IAuthReducerState> = (state = initialState, action) => {
  switch (action.type) {
    case "SET_LIQUID_VALIDATION": {
      let finalValidation = action.payload.validation;
      if (finalValidation?.hasSignature) {
        setInLocalStorage(validationInLocalStorageKey, JSON.stringify(action.payload.validation));
      } else {
        const validationFromLocalStorage = getFromLocalStorage(validationInLocalStorageKey);
        if (validationFromLocalStorage) {
          try {
            finalValidation = JSON.parse(validationFromLocalStorage);
          } catch (error) {
            logger.error(error);
          }
        }
      }

      return {
        ...state,
        validation: finalValidation,
      };
    }
    case "SET_LIQUID_CUSTOMER": {
      return {
        ...state,
        liquidCustomer: action.payload.liquidCustomer,
      };
    }
    case "LOADING_SESSION": {
      return {
        ...state,
        isLoading: true,
        user: null,
        customerRelationship: null,
        organization: null,
      };
    }
    case "LOADING_SESSION_ERROR": {
      return {
        ...state,
        isLoading: false,
        user: null,
        customerRelationship: null,
        organization: null,
      };
    }

    case "LOADED_SESSION": {
      return {
        ...state,
        isLoading: false,
        customerRelationship: action.payload.customerRelationship,
        organization: action.payload.organization,
      };
    }

    case "LOADED_THEME": {
      if (!action.payload.theme) {
        return state;
      }

      return {
        ...state,
        theme: {
          ...state.theme,
          ...action.payload.theme,
        },
      };
    }

    case "SET_DEFAULT_CUSTOMER_ADDRESS": {
      return {
        ...state,
        isLoading: false,
        customerRelationship: {
          ...state.customerRelationship!,
          defaultAddress: action.payload.defaultCustomerAddress,
        },
      };
    }

    case "DELETED_CUSTOMER_ADDRESS": {
      return {
        ...state,
        isLoading: false,
        customerRelationship: {
          ...state.customerRelationship!,
          defaultAddress:
            action.payload.deletedCustomerAddressId === state.customerRelationship?.defaultAddress?.id
              ? undefined
              : state.customerRelationship?.defaultAddress,
        },
      };
    }

    case "ADD_PAYMENT_METHOD_SUCCESS": {
      return {
        ...state,
        isLoading: false,
        // paymentMethods: state.paymentMethods.concat(
        //   action.payload.paymentMethod
        // ),
      };
    }

    case "LOADED_PAYMENT_METHODS": {
      return {
        ...state,
        isLoading: false,
        paymentMethods: action.payload.paymentMethods,
      };
    }

    case "CHANGING_CUSTOMER_INFORMATION": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "CHANGING_CUSTOMER_INFORMATION_ERROR": {
      return {
        ...state,
        isLoading: false,
      };
    }

    case "CHANGING_CUSTOMER_INFORMATION_SUCCESS": {
      return {
        ...state,
        isLoading: false,
        liquidCustomer: {
          ...(state.liquidCustomer as IShopifyLiquidCustomer),
          email: action.payload.customer.email as string,
        },
        customerRelationship: {
          ...(state.customerRelationship as ICustomerRelationship),
          email: action.payload.customer.email as string,
        },
      };
    }

    case "UPDATED_CUSTOMER_LOYALTY_POINTS": {
      return {
        ...state,
        customerRelationship: {
          ...(state.customerRelationship as ICustomerRelationship),
          orgPoints: action.payload.points,
        },
      };
    }

    default: {
      return state;
    }
  }
};
